import { useCallback, useEffect, useRef, useState } from 'react';
import type { PlaybackState, ResolvedTrack } from '../types';

const YOUTUBE_IFRAME_API_SRC = 'https://www.youtube.com/iframe_api';

interface YouTubePlayerError {
  id: number;
  message: string;
  videoId?: string | null;
}

interface YouTubePlayerState {
  playbackState: PlaybackState;
  isPlaying: boolean;
  currentTrackIndex: number;
  currentArtworkUrl: string | null;
  currentVideoId: string | null;
  error: YouTubePlayerError | null;
}

interface UseYouTubePlayerReturn extends YouTubePlayerState {
  setQueue: (tracks: ResolvedTrack[]) => void;
  play: () => Promise<void>;
  pause: () => void;
  skipToNext: () => Promise<void>;
  skipToPrevious: () => Promise<void>;
  skipToTrack: (index: number) => Promise<void>;
  reset: () => void;
  clearError: () => void;
}

interface UseYouTubePlayerOptions {
  containerId: string;
  isVisible: boolean;
}

const mapPlayerState = (state: number): PlaybackState => {
  if (!window.YT?.PlayerState) return 'none';

  switch (state) {
    case window.YT.PlayerState.PLAYING:
      return 'playing';
    case window.YT.PlayerState.PAUSED:
    case window.YT.PlayerState.CUED:
      return 'paused';
    case window.YT.PlayerState.BUFFERING:
      return 'loading';
    case window.YT.PlayerState.ENDED:
      return 'stopped';
    default:
      return 'none';
  }
};

const getVideoIdForTrack = (track?: ResolvedTrack): string | null => {
  if (!track) return null;
  const candidate = track.youtubeVideoId ?? track.catalogId;
  return candidate && candidate.trim().length > 0 ? candidate : null;
};

const getThumbnailUrl = (videoId: string): string => `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

export function useYouTubePlayer({ containerId, isVisible }: UseYouTubePlayerOptions): UseYouTubePlayerReturn {
  const [state, setState] = useState<YouTubePlayerState>({
    playbackState: 'none',
    isPlaying: false,
    currentTrackIndex: 0,
    currentArtworkUrl: null,
    currentVideoId: null,
    error: null,
  });

  const playerRef = useRef<YT.Player | null>(null);
  const playerReadyRef = useRef(false);
  const tracksRef = useRef<ResolvedTrack[]>([]);
  const currentTrackIndexRef = useRef(0);
  const currentVideoIdRef = useRef<string | null>(null);
  const pendingLoadRef = useRef<{ index: number; autoplay: boolean } | null>(null);
  const failedVideoIdsRef = useRef<Set<string>>(new Set());
  const errorSequenceRef = useRef(0);
  const apiLoadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearApiLoadTimeout = useCallback(() => {
    if (apiLoadTimeoutRef.current) {
      clearTimeout(apiLoadTimeoutRef.current);
      apiLoadTimeoutRef.current = null;
    }
  }, []);

  const reportError = useCallback((message: string, videoId?: string | null) => {
    errorSequenceRef.current += 1;
    setState(prev => ({
      ...prev,
      error: {
        id: errorSequenceRef.current,
        message,
        videoId,
      },
    }));
  }, []);

  const loadTrackAtIndex = useCallback((index: number, autoplay: boolean) => {
    const tracks = tracksRef.current;
    if (index < 0 || index >= tracks.length) {
      setState(prev => ({
        ...prev,
        playbackState: 'stopped',
        isPlaying: false,
      }));
      return;
    }

    const track = tracks[index];
    const videoId = getVideoIdForTrack(track);
    if (!videoId) {
      currentVideoIdRef.current = null;
      setState(prev => ({
        ...prev,
        playbackState: 'stopped',
        isPlaying: false,
      }));
      return;
    }

    currentTrackIndexRef.current = index;
    currentVideoIdRef.current = videoId;

    setState(prev => ({
      ...prev,
      playbackState: autoplay ? 'loading' : 'paused',
      isPlaying: false,
      currentTrackIndex: index,
      currentVideoId: videoId,
      currentArtworkUrl: track.artworkUrl ?? getThumbnailUrl(videoId),
    }));

    if (playerRef.current && playerReadyRef.current) {
      if (autoplay) {
        playerRef.current.loadVideoById(videoId);
      } else {
        playerRef.current.cueVideoById(videoId);
      }
    } else {
      pendingLoadRef.current = { index, autoplay };
    }
  }, []);

  const skipToNextInternal = useCallback(async () => {
    const nextIndex = currentTrackIndexRef.current + 1;
    if (nextIndex >= tracksRef.current.length) {
      setState(prev => ({
        ...prev,
        playbackState: 'stopped',
        isPlaying: false,
      }));
      return;
    }

    loadTrackAtIndex(nextIndex, true);
  }, [loadTrackAtIndex]);

  useEffect(() => {
    if (window.YT?.Player) {
      return;
    }

    clearApiLoadTimeout();
    apiLoadTimeoutRef.current = setTimeout(() => {
      if (!window.YT?.Player) {
        reportError('YouTube player failed to load. Check blockers and try again.');
      }
    }, 12000);

    const existingScript = document.querySelector(`script[src="${YOUTUBE_IFRAME_API_SRC}"]`);
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = YOUTUBE_IFRAME_API_SRC;
      script.async = true;
      script.onerror = () => {
        reportError('YouTube player failed to load. Check blockers and try again.');
      };
      document.head.appendChild(script);
    }

    return () => {
      clearApiLoadTimeout();
    };
  }, [clearApiLoadTimeout, reportError]);

  useEffect(() => {
    if (!isVisible) {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      playerReadyRef.current = false;
      return;
    }

    const handlePlayerReady = () => {
      playerReadyRef.current = true;
      clearApiLoadTimeout();
      if (pendingLoadRef.current && playerRef.current) {
        const { index, autoplay } = pendingLoadRef.current;
        const track = tracksRef.current[index];
        const videoId = getVideoIdForTrack(track);
        if (videoId) {
          if (autoplay) {
            playerRef.current.loadVideoById(videoId);
          } else {
            playerRef.current.cueVideoById(videoId);
          }
        }
        pendingLoadRef.current = null;
      }
    };

    const handleStateChange = (event: YT.OnStateChangeEvent) => {
      if (window.YT?.PlayerState && event.data === window.YT.PlayerState.ENDED) {
        void skipToNextInternal();
        return;
      }

      const playbackState = mapPlayerState(event.data);
      setState(prev => ({
        ...prev,
        playbackState,
        isPlaying: playbackState === 'playing',
      }));
    };

    const handleError = () => {
      const currentIndex = currentTrackIndexRef.current;
      const track = tracksRef.current[currentIndex];
      const videoId = currentVideoIdRef.current ?? getVideoIdForTrack(track);

      if (videoId) {
        if (!failedVideoIdsRef.current.has(videoId)) {
          failedVideoIdsRef.current.add(videoId);
          const trackLabel = track?.title ? `"${track.title}"` : 'this track';
          reportError(`YouTube couldn't play ${trackLabel}. Skipping to the next track.`, videoId);
        }
      } else {
        reportError('YouTube playback error. Skipping to the next track.');
      }
      void skipToNextInternal();
    };

    const initializePlayer = () => {
      if (playerRef.current) return;
      const container = document.getElementById(containerId);
      if (!container || !window.YT?.Player) return;

      playerRef.current = new window.YT.Player(containerId, {
        width: '100%',
        height: '100%',
        playerVars: {
          autoplay: 0,
          controls: 0,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          fs: 0,
          iv_load_policy: 3,
          disablekb: 1,
          origin: window.location.origin,
        },
        events: {
          onReady: handlePlayerReady,
          onStateChange: handleStateChange,
          onError: handleError,
        },
      });
    };

    if (window.YT?.Player) {
      initializePlayer();
      return;
    }

    const previousReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previousReady?.();
      initializePlayer();
    };
  }, [containerId, isVisible, skipToNextInternal, clearApiLoadTimeout, reportError]);

  const setQueue = useCallback((tracks: ResolvedTrack[]) => {
    tracksRef.current = tracks;
    failedVideoIdsRef.current.clear();
    setState(prev => ({ ...prev, error: null }));

    if (tracks.length === 0) {
      currentVideoIdRef.current = null;
      setState(prev => ({
        ...prev,
        playbackState: 'none',
        isPlaying: false,
        currentTrackIndex: 0,
        currentVideoId: null,
        currentArtworkUrl: null,
        error: null,
      }));
      return;
    }

    loadTrackAtIndex(0, false);
  }, [loadTrackAtIndex]);

  const play = useCallback(async () => {
    const player = playerRef.current;
    const currentIndex = currentTrackIndexRef.current;
    const currentTrack = tracksRef.current[currentIndex];
    const videoId = getVideoIdForTrack(currentTrack);

    if (!videoId) return;

    if (player && playerReadyRef.current) {
      const loadedId = player.getVideoData?.().video_id;
      if (loadedId === videoId) {
        player.playVideo();
        setState(prev => ({ ...prev, playbackState: 'loading' }));
        return;
      }
      loadTrackAtIndex(currentIndex, true);
      return;
    }

    setState(prev => ({ ...prev, playbackState: 'loading' }));
    pendingLoadRef.current = { index: currentIndex, autoplay: true };
  }, [loadTrackAtIndex]);

  const pause = useCallback(() => {
    const player = playerRef.current;
    if (!player || !playerReadyRef.current) return;
    player.pauseVideo();
  }, []);

  const skipToNext = useCallback(async () => {
    await skipToNextInternal();
  }, [skipToNextInternal]);

  const skipToPrevious = useCallback(async () => {
    const prevIndex = Math.max(0, currentTrackIndexRef.current - 1);
    loadTrackAtIndex(prevIndex, true);
  }, [loadTrackAtIndex]);

  const skipToTrack = useCallback(async (index: number) => {
    loadTrackAtIndex(index, true);
  }, [loadTrackAtIndex]);

  const reset = useCallback(() => {
    if (playerRef.current && playerReadyRef.current) {
      playerRef.current.stopVideo();
    }
    tracksRef.current = [];
    currentTrackIndexRef.current = 0;
    currentVideoIdRef.current = null;
    pendingLoadRef.current = null;
    failedVideoIdsRef.current.clear();
    setState({
      playbackState: 'none',
      isPlaying: false,
      currentTrackIndex: 0,
      currentArtworkUrl: null,
      currentVideoId: null,
      error: null,
    });
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    setQueue,
    play,
    pause,
    skipToNext,
    skipToPrevious,
    skipToTrack,
    reset,
    clearError,
  };
}
